<script setup lang="ts">
  import { useNotifications } from '~/composables/use-notifications'

  withDefaults(
    defineProps<{
      placement?: 'top' | 'right' | 'bottom' | 'left'
      collapsed?: boolean
    }>(),
    { placement: 'right', collapsed: false }
  )

  const router = useRouter()
  const route = useRoute()
  const { t } = useI18n()
  const toast = useToast()

  const {
    unreadCount,
    previewItems,
    previewLoading,
    previewError,
    fetchPreview,
    markAsRead,
    markAllAsRead,
    startPolling,
    stopPolling,
  } = useNotifications()

  const open = ref(false)
  const activeTab = ref('all')
  const markAllBusy = ref(false)

  const tabs = computed(() => [
    { value: 'all', label: t('notifications.all') },
    { value: 'unread', label: t('notifications.unread') },
  ])

  const unreadLabel = computed(() => (unreadCount.value > 99 ? '99+' : String(unreadCount.value)))

  const isActive = computed(() => route.path === '/notifications')

  function loadPreview() {
    return fetchPreview({ unread: activeTab.value === 'unread' })
  }

  function setTab(value: string) {
    activeTab.value = value
    loadPreview()
  }

  async function onMarkAll() {
    markAllBusy.value = true
    try {
      await markAllAsRead()
      await loadPreview()
      toast.add({
        title: t('notifications.allMarkedRead'),
        color: 'success',
      })
    } catch (err) {
      const error = err as { data?: { message?: string }; message?: string }
      toast.add({
        title: t('notifications.readError'),
        description: error?.data?.message || error?.message,
        color: 'error',
      })
    } finally {
      markAllBusy.value = false
    }
  }

  async function onItemClick(notification: { id: string; actionUrl?: string | null }) {
    try {
      await markAsRead(notification.id)
    } catch (err) {
      const error = err as { data?: { message?: string }; message?: string }
      toast.add({
        title: t('notifications.readError'),
        description: error?.data?.message || error?.message,
        color: 'error',
      })
    }

    open.value = false

    if (notification.actionUrl) {
      router.push(notification.actionUrl)
    }
  }

  function showAll() {
    open.value = false
    router.push('/notifications')
  }

  onMounted(() => {
    startPolling()
  })

  onUnmounted(() => {
    stopPolling()
  })

  watch(open, (value) => {
    if (value) {
      loadPreview()
    }
  })
</script>

<template>
  <UPopover
    v-model:open="open"
    :content="{ side: placement, align: placement === 'bottom' ? 'end' : 'start' }"
    :ui="{ content: 'z-[70] w-[min(92vw,380px)] p-0 overflow-hidden' }"
  >
    <button
      type="button"
      class="group relative flex w-full items-center gap-2 rounded-[10px] px-2 py-2 text-base transition-colors hover:bg-elevated/60"
      :class="[
        collapsed ? 'justify-center px-0' : '',
        isActive ? 'bg-primary/10 text-primary' : 'text-default',
      ]"
      :aria-label="t('notifications.title')"
      :title="collapsed ? t('notifications.title') : undefined"
    >
      <UIcon name="i-lucide-bell" class="size-6 min-w-6" />

      <span v-if="!collapsed" class="flex-1 text-left">{{ t('notifications.title') }}</span>

      <UBadge
        v-if="unreadCount > 0"
        color="error"
        variant="solid"
        size="xs"
        class="justify-center px-1"
        :class="collapsed ? 'absolute top-1 right-1 min-w-4' : 'min-w-5'"
      >
        {{ unreadLabel }}
      </UBadge>
    </button>

    <template #content>
      <div class="flex items-center justify-between gap-2 border-b border-default px-4 py-3">
        <div class="flex items-center gap-2">
          <span class="text-base font-semibold text-highlighted">{{ t('notifications.title') }}</span>

          <UBadge v-if="unreadCount > 0" color="primary" variant="subtle" size="sm">
            {{ unreadCount }}
          </UBadge>
        </div>

        <UButton
          variant="ghost"
          color="neutral"
          size="xs"
          :disabled="unreadCount === 0 || markAllBusy"
          :loading="markAllBusy"
          @click="onMarkAll"
        >
          {{ t('notifications.markAll') }}
        </UButton>
      </div>

      <div class="flex gap-1 border-b border-default px-3 pt-2">
        <button
          v-for="tab in tabs"
          :key="tab.value"
          type="button"
          class="rounded-t-lg px-3 py-2 text-sm font-medium transition-colors"
          :class="
            activeTab === tab.value
              ? 'border-b-2 border-primary text-primary'
              : 'text-muted hover:text-default'
          "
          @click="setTab(tab.value)"
        >
          {{ tab.label }}
        </button>
      </div>

      <div class="max-h-[min(70vh,420px)] overflow-y-auto p-2">
        <div v-if="previewLoading" class="space-y-2 p-1">
          <USkeleton v-for="index in 3" :key="index" class="h-16 w-full rounded-xl" />
        </div>

        <div v-else-if="previewError" class="flex flex-col items-center gap-2 px-4 py-8 text-center">
          <UIcon name="i-lucide-wifi-off" class="size-7 text-muted" />

          <p class="text-sm text-muted">{{ previewError }}</p>

          <UButton variant="soft" size="sm" @click="loadPreview">
            {{ t('notifications.retry') }}
          </UButton>
        </div>

        <template v-else-if="previewItems.length">
          <NotificationItem
            v-for="notification in previewItems"
            :key="notification.id"
            :notification="notification"
            compact
            @click="onItemClick(notification)"
          />
        </template>

        <div v-else class="flex flex-col items-center gap-2 px-4 py-10 text-center">
          <UIcon name="i-lucide-bell-off" class="size-8 text-muted" />

          <p class="text-sm text-muted">
            {{ activeTab === 'unread' ? t('notifications.emptyUnread') : t('notifications.empty') }}
          </p>
        </div>
      </div>

      <div class="border-t border-default p-2">
        <UButton
          variant="ghost"
          color="neutral"
          class="w-full justify-center"
          @click="showAll"
        >
          {{ t('notifications.showAll') }}
        </UButton>
      </div>
    </template>
  </UPopover>
</template>
